<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">   
                    <v-btn dark icon title="Back to previous page" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>                 
                    <v-spacer></v-spacer>
                    <help-link appId="finance" pageId="reimbursements-other-employee" color="white"></help-link>
                    <v-btn v-if="hasAddRights" fab dark small color="primary" title="Create Reimbursements for other Employees" @click="showAdd">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-card class="elevation-1 mt-5"> 
                <v-toolbar flat height="45px">
                    <div :class="isMobile ? 'wp-60 mb-10' : 'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search name, type, status" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="headers"
                    :items="reimbursements_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile" @click="viewDetails(props.item)">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1">
                                    <v-toolbar flat height="42">
                                        <v-chip @click="viewUserProfile(props.item, $store.state.userId)" class="user-chip-container">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.name}} 
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout">
                                        <v-flex xs12 class="text-xs-left mt-5 mb-2">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Type</div>
                                                    <div>{{props.item.reimbursementType}}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Amount</div>
                                                    <div>₱{{numFormat.formatNumber(props.item.amount)}}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Created On</div>
                                                    <div>{{props.item.createdOn | moment('MMM DD, YYYY')}}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Status</div>
                                                    <div :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else @click="viewDetails(props.item)" title="Click to view details" class="clickable">
                            <td>
                                <v-chip @click="viewUserProfile(props.item, $store.state.userId)" class="user-chip-container">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.name}} 
                                </v-chip>
                            </td>
                            <td>{{props.item.reimbursementType}}</td>
                            <td>₱{{numFormat.formatNumber(props.item.amount)}}</td>
                            <td>{{props.item.createdOn | moment('MMM DD, YYYY')}}</td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</b>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>
        
        <v-dialog v-model="dialogAddEdit" width="500" persistent>   
            <add-edit ref="addEditRef" :dialogAddEdit.sync="dialogAddEdit" @send="addHandler"></add-edit>
        </v-dialog>
    </v-layout> 
</template>

<script>

import reimbursementsApi from '@/api/reimbursements'
import commonfn from '@/common/functions'           //for refactor
import numFormat from '@/common/num_format'
import pageKeys from '@/common/pagekeys'
import awsS3Helper from '@/helpers/aws-s3'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getSort, getRowsPerPage, getSearchKeyword, updateRowsPerPage, updateSort, updateSearchKeyword } from '@/helpers/user-prefs'
import { hasAddRights, hasEditRights } from '@/helpers/access-permissions'

export default {     
    name: 'Reimbursements',
    metaInfo: {
        title: 'Reimbursements',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View, create Reimbursements' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-edit': () => import('@/components/Reimbursements/AddEditReimbursements')
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = getSort(routeName)
        
        return {
            commonfn,
            numFormat,
            awsS3Helper,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: getSearchKeyword(routeName),
            error: false,
            errorMessage: '',
            loading: false,
            dialogAddEdit: false,
            headers: [
                {text: 'Name', align: 'left', value:'name', width: '20%', sortable:true},
                {text: 'Type', align: 'left', value:'reimbursementType', width: '20%', sortable:true},
                {text: 'Amount', align: 'left', value: 'amount',  width: '15%', sortable:true},
                {text: 'Created On', align: 'left', value: 'createdOn',  width: '15%', sortable:true},
                {text: 'Status', align: 'left', value: 'status',  width: '15%', sortable:true},
            ],
            reimbursements: []
        }
    },
    methods: {
        showAdd() {
            const data = {
                isRequestForOtherUser: true,
                pageKey: pageKeys.reimbursements
            }
            this.$refs.addEditRef.setAdd(data)
            this.dialogAddEdit = true
        },
        viewDetails(data) {
            if (data.userId == this.$store.state.userId) {
                this.$router.push(`/finance/myreimbursements/${data.reimbursementsId}`)
            } else {
                this.$router.push(`/finance/reimbursements/${data.reimbursementsId}`)
            } 
        },
        addHandler(reimbursementsId) {
            this.$router.push(`/finance/reimbursements/${reimbursementsId}`)
        },
        async getReimbursements(show_loading) {
            try {
                this.loading = show_loading
                const response = await reimbursementsApi.getAll()
                if (response.status == 200) {
                    this.reimbursements = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Reimbursements'
        })

        this.getReimbursements(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        reimbursements_filtered() {
            var temp = this.reimbursements
            if (this.search) {
                temp = temp.filter(item => {
                    const searchLower = this.search.toLowerCase()
                    const nameMatch = item.name.toLowerCase().includes(searchLower)
                    const typeMatch = item.reimbursementType.toLowerCase().includes(searchLower)
                    const statusMatch = commonfn.getStatusDescription(item.status).toLowerCase().includes(searchLower) 
                    return nameMatch || typeMatch || statusMatch
                })
            }
            return temp
        },
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 23px 0px 23px;
}
</style>